import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  HelpCircle,
  Phone,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  BookOpen,
  Download,
} from 'lucide-react';
import { getBaseUrl } from '@/utils/api';

const API_URL = getBaseUrl();

const HelpPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('faq');
  const [faqItems, setFaqItems] = useState([]);
  const [resources, setResources] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [faqRes, resourcesRes] = await Promise.all([
        fetch(`${API_URL}/api/support/faq`),
        fetch(`${API_URL}/api/support/resources`)
      ]);

      if (faqRes.ok) {
        const data = await faqRes.json();
        setFaqItems(data.items || []);
      }

      if (resourcesRes.ok) {
        setResources(await resourcesRes.json());
      }
    } catch (error) {
      console.error('Error fetching support data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group FAQ by category
  const faqByCategory = faqItems.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-mist flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burnt-terra"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory-mist">
      {/* Header */}
      <header className="border-b border-burnt-terra/20 bg-white/80 backdrop-blur-sm sticky top-0 z-10 pt-safe">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => {
              // Navigate history back, but if there's no history (fresh
              // load from home screen icon), fall back to the client hub so
              // the user is never stranded on the Help page.
              if (window.history.length > 1) navigate(-1);
              else navigate('/client/hub');
            }}
            className="text-shadow-pine hover:text-burnt-terra -ml-2"
            data-testid="help-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="font-display text-xl font-bold text-obsidian-night">Help & Support</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveSection('faq')}
            className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeSection === 'faq'
                ? 'bg-burnt-terra text-white'
                : 'bg-white text-shadow-pine hover:bg-ivory-mist'
            }`}
          >
            <HelpCircle className="w-4 h-4" />
            FAQ
          </button>
          <button
            onClick={() => setActiveSection('safety')}
            className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeSection === 'safety'
                ? 'bg-burnt-terra text-white'
                : 'bg-white text-shadow-pine hover:bg-ivory-mist'
            }`}
          >
            <Phone className="w-4 h-4" />
            Safety
          </button>
          <button
            onClick={() => setActiveSection('references')}
            data-testid="help-tab-references"
            className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeSection === 'references'
                ? 'bg-burnt-terra text-white'
                : 'bg-white text-shadow-pine hover:bg-ivory-mist'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            References
          </button>
        </div>

        {/* FAQ Section */}
        {activeSection === 'faq' && (
          <div className="space-y-6">
            {Object.entries(faqByCategory).map(([category, items]) => (
              <div key={category}>
                <h3 className="font-display font-bold text-obsidian-night mb-3">{category}</h3>
                <div className="space-y-2">
                  {items.map(item => (
                    <div key={item.id} className="bg-white rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === item.id ? null : item.id)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left"
                      >
                        <span className="font-medium text-obsidian-night pr-4">{item.question}</span>
                        {expandedFaq === item.id ? (
                          <ChevronUp className="w-4 h-4 text-shadow-pine flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-shadow-pine flex-shrink-0" />
                        )}
                      </button>
                      {expandedFaq === item.id && (
                        <div className="px-4 pb-4 text-sm text-shadow-pine/80 leading-relaxed">
                          {item.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* References Section */}
        {activeSection === 'references' && (
          <div className="space-y-4" data-testid="help-references">
            {/* Downloadable handbook — the exhaustive toolset + rubric PDF */}
            <a
              href="/GNTL_STRNGTH_Toolset_Handbook.pdf"
              download
              target="_blank"
              rel="noopener noreferrer"
              data-testid="help-references-handbook-download"
              className="block bg-burnt-terra text-white rounded-xl p-5 hover:bg-burnt-terra/90 transition-colors"
            >
              <div className="flex items-start gap-3">
                <Download className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold">Toolset &amp; Rubric Handbook (PDF)</div>
                  <div className="text-sm text-white/85 mt-1 leading-relaxed">
                    Full outline of every tool — Body, Voice, Story, Operation Listen — and how they
                    compound. Tap to download.
                  </div>
                </div>
              </div>
            </a>

            <div className="bg-white rounded-xl p-6">
              <h3 className="font-display font-bold text-obsidian-night mb-4">References</h3>
              <ul className="space-y-4 text-sm text-shadow-pine/85 leading-relaxed">
                <li>
                  The Listening Chain is adapted from Roos, Postmes &amp; Koudenburg (2023), <em>PLOS ONE</em>.{' '}
                  <a
                    href="https://doi.org/10.1371/journal.pone.0292865"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-burnt-terra hover:underline break-words"
                    data-testid="help-references-listening-chain-link"
                  >
                    doi.org/10.1371/journal.pone.0292865
                  </a>
                  . CC BY 4.0.
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Safety Section */}
        {activeSection === 'safety' && resources && (
          <div className="space-y-6">
            {/* Important Notice */}
            <div className="bg-burnt-terra/10 border border-burnt-terra/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-burnt-terra flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-obsidian-night">{resources.boundaries?.title}</h3>
                  <p className="text-sm text-burnt-terra mt-1">{resources.boundaries?.message}</p>
                </div>
              </div>
            </div>

            {/* Crisis Resources */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-display font-bold text-obsidian-night mb-4">Crisis Resources</h3>
              <p className="text-sm text-shadow-pine/70 mb-4">
                If you or someone you know is in crisis, please reach out to these resources.
              </p>
              
              <div className="space-y-3">
                {resources.crisis_resources?.map((resource, idx) => (
                  <div key={idx} className="p-4 bg-ivory-mist rounded-lg">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-bold text-obsidian-night">{resource.name}</p>
                        <p className="text-sm text-shadow-pine/70">{resource.description}</p>
                        <p className="text-burnt-terra font-medium mt-1">{resource.contact}</p>
                      </div>
                      {resource.region && (
                        <span className="text-xs bg-shadow-pine/10 text-shadow-pine px-2 py-1 rounded-full">
                          {resource.region}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Help */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-display font-bold text-obsidian-night mb-2">Need Immediate Help?</h3>
              <p className="text-sm text-shadow-pine/70 mb-4">
                If you&apos;re experiencing a mental health emergency, please contact emergency services 
                or go to your nearest emergency room.
              </p>
              <p className="text-sm text-shadow-pine/70">
                For non-emergency support, reach out at{' '}
                <a href={`mailto:${resources.contact_email}`} className="text-burnt-terra hover:underline">
                  {resources.contact_email}
                </a>
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default HelpPage;
